import React, { useEffect, useMemo, useRef, useState } from "react";

import { Map as MapGL, Source, Layer, Marker, NavigationControl }
  from "@vis.gl/react-maplibre";

import maplibregl from "maplibre-gl";

import * as topojson from "topojson-client";
import { geoCentroid } from "d3-geo";
import { scaleQuantize } from "d3-scale";
import { AnimatePresence, motion } from "framer-motion";
import stateFillScale from "../../../../Component/AdminComponents/Map Components/stateFillScale";
import StatusPill from "../../../../Component/AdminComponents/Map Components/StatusPill";

import cn from "../../../../Component/AdminComponents/Map Components/cn";
import flattenEmployeePayload from "../../../../Component/AdminComponents/Map Components/flattenEmployeePayload";
import normalizeStateName from "../../../../Component/AdminComponents/Map Components/normalizeStateName";
import normalizeDistrictName from "../../../../Component/AdminComponents/Map Components/normalizeDistrictName";
import getTopoObject from "../../../../Component/AdminComponents/Map Components/getTopoObject";
import deriveStatus from "../../../../Component/AdminComponents/Map Components/deriveStatus";
import { BASE_URL } from "../../../../config/api";
import Card from "../../../../Component/AdminComponents/Map Components/Card";
import { ValueLoader } from "../../../../Component/Common/ValueLoader";
import axios from "axios";


// -------------------- MAP STYLE --------------------
// Free public MapLibre demo style (no key)
const MAP_STYLE = "https://demotiles.maplibre.org/style.json";

// -------------------- TOPOJSON URLS --------------------
const INDIA_STATES_TOPOJSON_URL =
  "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@8d907bc/topojson/india.json";

function stateToDistrictTopoUrl(stateName) {
  const slug = String(stateName || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-");
  return `https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@8d907bc/topojson/states/${slug}.json`;
}
// -------------------- MAIN --------------------
export default function OpsAllocationMapLibre() {
  const mapRef = useRef(null);

  const [selectedState, setSelectedState] = useState("All India");
  const [hover, setHover] = useState(null);

  const [mapDashboard, setMapDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);


  // controls
  const [globalQuery, setGlobalQuery] = useState("");
  const [districtQuery, setDistrictQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showPins, setShowPins] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });


  // topojson/geojson
  const [indiaGeo, setIndiaGeo] = useState(null); // states geojson
  const [districtGeo, setDistrictGeo] = useState(null); // districts geojson for selected state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [employeeApiData, setEmployeeApiData] = useState([]);
  const [shopApiData, setShopApiData] = useState([]);



  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const companyId = authUser?.company_id || authUser?.id;

  useEffect(() => {

    if (!companyId) return;

    let alive = true;

    (async () => {

      try {

        setLoading(true);

        setErr(null);

        const response =
          await axios.get(
            `${BASE_URL}company/bharat-data`,
            {
              params: {
                company_id:
                  companyId,
              },

              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },
            }
          );

        const json =
          response.data;

        if (
          alive &&
          json?.success &&
          Array.isArray(
            json?.data
          )
        ) {

          setEmployeeApiData(
            json.data
          );

        } else {

          setEmployeeApiData([]);
        }

      } catch (e) {

        if (alive) {

          setErr(
            e?.response?.data
              ?.message ||
            e?.message ||
            "Failed to load employee allocation data"
          );

          setEmployeeApiData([]);
        }

      } finally {

        if (alive) {
          setLoading(false);
        }
      }

    })();

    return () => {
      alive = false;
    };

  }, [companyId]);

useEffect(() => {

  let alive = true;

  (async () => {

    try {

      setLoading(true);

      setErr(null);

      const response =
        await axios.get(
          `${BASE_URL}company/state-district-shop-data`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      if (
        alive &&
        json?.success &&
        Array.isArray(
          json?.data
        )
      ) {

        setShopApiData(
          json.data
        );

      } else {

        setShopApiData([]);
      }

    } catch (e) {

      if (alive) {

        setErr(
          e?.response?.data
            ?.message ||
          e?.message ||
          "Failed to load shop coverage data"
        );

        setShopApiData([]);
      }

    } finally {

      if (alive) {
        setLoading(false);
      }
    }

  })();

  return () => {
    alive = false;
  };

}, []);


  const fetchMapDashboard = async () => {

    if (!companyId) return;

    try {

      setDashboardLoading(true);

      const response =
        await axios.get(
          `${BASE_URL}warranty/company-map-dashboard`,
          {
            params: {
              company_id:
                companyId,
            },

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      if (json?.status) {

        setMapDashboard(
          json.data
        );

      } else {

        setMapDashboard(null);
      }

    } catch (e) {

      console.error(
        "Map dashboard API failed",
        e
      );

      setMapDashboard(null);

    } finally {

      setDashboardLoading(false);

    }
  };


useEffect(() => {
  if (!companyId) return;

  fetchMapDashboard();
}, [companyId]);



  // ---------- DATA NORMALIZATION ----------
  const districtShops = useMemo(() => {
    return (shopApiData || []).map((d) => ({
      state: normalizeStateName(d.state),
      district: normalizeDistrictName(d.district),
      shops: Number(d.shops || 0),
      lat: d.lat, // optional future support
      lng: d.lng,
    }));
  }, [shopApiData]);


  const assignments = useMemo(
    () => flattenEmployeePayload(employeeApiData),
    [employeeApiData]
  );


  const assignmentByDistrict = useMemo(() => {
    const m = new Map();
    for (const a of assignments) {
      const key = `${a.state}__${a.district}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    }
    return m;
  }, [assignments]);

  const shopsByDistrict = useMemo(() => {
    const m = new Map();
    for (const d of districtShops) {
      const key = `${d.state}__${d.district}`;
      const prev = m.get(key) || { ...d, shops: 0 };
      prev.shops += d.shops;
      if (d.lat != null) prev.lat = d.lat;
      if (d.lng != null) prev.lng = d.lng;
      m.set(key, prev);
    }
    return m;
  }, [districtShops]);

  const stateTotals = useMemo(() => {
    const m = new Map();
    for (const d of districtShops) {
      if (!m.has(d.state)) m.set(d.state, { state: d.state, shops: 0 });
      m.get(d.state).shops += d.shops;
    }
    const assignedStates = new Set(assignments.map((a) => a.state));
    return [...m.values()]
      .map((x) => ({ ...x, hasAssignments: assignedStates.has(x.state) }))
      .sort((a, b) => b.shops - a.shops);
  }, [districtShops, assignments]);

  const allIndia = useMemo(() => {
    const totalShops = districtShops.reduce((s, d) => s + d.shops, 0);
    const statesCovered = new Set(districtShops.map((d) => d.state)).size;
    const districtsCovered = new Set(districtShops.map((d) => `${d.state}__${d.district}`)).size;
    const employees = new Set(assignments.map((a) => a.name)).size;
    const managers = new Set(
      assignments.filter((a) => String(a.position).toLowerCase().includes("manager")).map((a) => a.name)
    ).size;
    const executives = new Set(
      assignments.filter((a) => String(a.position).toLowerCase().includes("executive")).map((a) => a.name)
    ).size;
    return { totalShops, statesCovered, districtsCovered, employees, managers, executives };
  }, [districtShops, assignments]);

  // ---------- LOAD INDIA STATES GEOJSON ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr(null);
        setLoading(true);
        const res = await fetch(INDIA_STATES_TOPOJSON_URL);
        if (!res.ok) throw new Error(`Failed to load India topojson (${res.status})`);
        const topo = await res.json();
        const obj = getTopoObject(topo);
        if (!obj) throw new Error("Invalid topojson: no objects found");
        const geo = topojson.feature(topo, obj);
        if (!alive) return;

        // decorate state names into a common property
        const decorated = {
          ...geo,
          features: (geo.features || []).map((f) => {
            const p = f.properties || {};
            const st =
              normalizeStateName(p.st_nm || p.ST_NM || p.state || p.STATE || p.name || p.NAME || p.NAME_1 || p.admin || p.ADMIN);
            return { ...f, properties: { ...p, __state: st } };
          }),
        };
        setIndiaGeo(decorated);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load India map");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---------- LOAD DISTRICTS GEOJSON FOR SELECTED STATE ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      if (selectedState === "All India") {
        setDistrictGeo(null);
        return;
      }
      try {
        setErr(null);
        setLoading(true);
        const url = stateToDistrictTopoUrl(selectedState);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to load district topojson (${res.status})`);
        const topo = await res.json();
        const obj = getTopoObject(topo);
        if (!obj) throw new Error("Invalid district topojson");
        const geo = topojson.feature(topo, obj);
        if (!alive) return;

        // Decorate district name + join shops/assignment info into properties
        const decorated = {
          ...geo,
          features: (geo.features || []).map((f) => {
            const p = f.properties || {};
            const rawDistrict =
              p.district || p.DISTRICT || p.dtname || p.DTNAME || p.district_name || p.DISTRICT_NAME || p.name || p.NAME || "";
            const district = normalizeDistrictName(rawDistrict);
            const key = `${selectedState}__${district}`;

            const shopRow = shopsByDistrict.get(key);
            const shops = shopRow?.shops ?? 0;
            const empList = assignmentByDistrict.get(key) || [];
            const status = deriveStatus({ shops, hasShopRow: !!shopRow, employeesCount: empList.length });

            // centroid for pin fallback (no need AREA_COORDS)
            const c = geoCentroid(f); // [lon, lat]

            return {
              ...f,
              properties: {
                ...p,
                __state: selectedState,
                __district: district,
                __shops: shops,
                __status: status,
                __empCount: empList.length,
                __centroidLon: Number.isFinite(c?.[0]) ? c[0] : null,
                __centroidLat: Number.isFinite(c?.[1]) ? c[1] : null,
              },
            };
          }),
        };

        setDistrictGeo(decorated);

        // Auto-fit to state bounds (smooth)
        const map = mapRef.current?.getMap?.();
        if (map && decorated?.features?.length) {
          // compute bbox from all coordinates (simple + safe)
          let minX = 180, minY = 90, maxX = -180, maxY = -90;
          for (const f of decorated.features) {
            const coords = f.geometry?.coordinates;
            if (!coords) continue;

            // walk coordinates (nested)
            const stack = [coords];
            while (stack.length) {
              const cur = stack.pop();
              if (!cur) continue;
              if (typeof cur[0] === "number" && typeof cur[1] === "number") {
                const [x, y] = cur;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
              } else if (Array.isArray(cur)) {
                for (const it of cur) stack.push(it);
              }
            }
          }

          if (minX < 180 && maxX > -180) {
            map.fitBounds(
              [
                [minX, minY],
                [maxX, maxY],
              ],
              { padding: 40, duration: 600 }
            );
          }
        }
      } catch (e) {
        if (!alive) return;
        setDistrictGeo(null);
        setErr(e?.message || `District map not available for ${selectedState}`);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedState, shopsByDistrict, assignmentByDistrict]);

  // ---------- COLOR SCALES ----------
  const maxStateShops = useMemo(() => Math.max(1, ...stateTotals.map((s) => s.shops || 0)), [stateTotals]);
  const stScale = useMemo(() => stateFillScale(maxStateShops), [maxStateShops]);

  const maxDistrictShops = useMemo(() => {
    if (!districtGeo?.features?.length) return 1;
    let m = 1;
    for (const f of districtGeo.features) m = Math.max(m, Number(f?.properties?.__shops || 0));
    return Math.max(1, m);
  }, [districtGeo]);

  const distScale = useMemo(() => {
    return scaleQuantize()
      .domain([0, Math.max(1, maxDistrictShops)])
      .range(["#eff6ff", "#dbeafe", "#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8"]);
  }, [maxDistrictShops]);

  // ---------- SEARCH ----------
  const globalQueryLower = globalQuery.trim().toLowerCase();

  const resolveSearchTarget = useMemo(() => {
    if (!globalQueryLower) return null;

    // state match
    const exactState = stateTotals.find((s) => String(s.state).toLowerCase() === globalQueryLower);
    if (exactState) return { type: "STATE", state: exactState.state };

    const partialState = stateTotals.find((s) => String(s.state).toLowerCase().includes(globalQueryLower));
    if (partialState) return { type: "STATE", state: partialState.state };

    // district match (shops first)
    const districtFromShops = districtShops.find((d) => String(d.district).toLowerCase().includes(globalQueryLower));
    if (districtFromShops) return { type: "DISTRICT", state: districtFromShops.state, district: districtFromShops.district };

    // district match (assign)
    const districtFromAssign = assignments.find((a) => String(a.district).toLowerCase().includes(globalQueryLower));
    if (districtFromAssign) return { type: "DISTRICT", state: districtFromAssign.state, district: districtFromAssign.district };

    return null;
  }, [globalQueryLower, stateTotals, districtShops, assignments]);

  const runGlobalSearch = () => {
    const t = resolveSearchTarget;
    if (!t) return;

    if (t.type === "STATE") {
      setSelectedState(t.state);
      setDistrictQuery("");
      setStatusFilter("ALL");
      setShowPins(true);
      return;
    }

    if (t.type === "DISTRICT") {
      setSelectedState(t.state);
      setDistrictQuery(t.district);
      setStatusFilter("ALL");
      setShowPins(true);
    }
  };

  // ---------- FILTER DISTRICT VIEW LIST ----------
  const filteredDistrictFeatures = useMemo(() => {
    if (!districtGeo?.features?.length) return [];

    const q = districtQuery.trim().toLowerCase();
    return districtGeo.features.filter((f) => {
      const p = f.properties || {};
      const district = String(p.__district || "").toLowerCase();
      const status = p.__status || "NO DATA";
      const okQuery = !q || district.includes(q);
      const okStatus = statusFilter === "ALL" ? true : status === statusFilter;
      return okQuery && okStatus;
    });
  }, [districtGeo, districtQuery, statusFilter]);

  // Build a filtered GeoJSON for the map (so only matching districts show strong opacity)
  const districtGeoFiltered = useMemo(() => {
    if (!districtGeo) return null;
    const set = new Set(filteredDistrictFeatures.map((f) => f.properties?.__district));
    return {
      ...districtGeo,
      features: districtGeo.features.map((f) => {
        const hit = set.has(f.properties?.__district);
        return { ...f, properties: { ...f.properties, __hit: hit } };
      }),
    };
  }, [districtGeo, filteredDistrictFeatures]);

  // ---------- PINS (use API lat/lng if present, else centroid) ----------
  const districtPins = useMemo(() => {
    if (selectedState === "All India") return [];
    const list = filteredDistrictFeatures.map((f) => {
      const p = f.properties || {};
      const district = p.__district;
      const key = `${selectedState}__${district}`;
      const empList = assignmentByDistrict.get(key) || [];
      const shops = Number(p.__shops || 0);
      const status = p.__status;

      // prefer shop/assignment lat/lng from API (if you provide later)
      const shopRow = shopsByDistrict.get(key);
      const anyAssign = empList[0];

      const lng = Number(shopRow?.lng ?? anyAssign?.lng);
      const lat = Number(shopRow?.lat ?? anyAssign?.lat);

      const hasApiCoord = Number.isFinite(lat) && Number.isFinite(lng);

      const cLon = p.__centroidLon;
      const cLat = p.__centroidLat;

      const finalLng = hasApiCoord ? lng : Number.isFinite(cLon) ? cLon : null;
      const finalLat = hasApiCoord ? lat : Number.isFinite(cLat) ? cLat : null;

      if (!Number.isFinite(finalLng) || !Number.isFinite(finalLat)) return null;

      return {
        district,
        state: selectedState,
        shops,
        status,
        employees: empList,
        lng: finalLng,
        lat: finalLat,
      };
    });
    return list.filter(Boolean);
  }, [selectedState, filteredDistrictFeatures, assignmentByDistrict, shopsByDistrict]);

  // ---------- MAP GEOJSON DECORATION (STATE VIEW COLORS) ----------
  const indiaGeoDecorated = useMemo(() => {
    if (!indiaGeo) return null;
    const totalsMap = new Map(stateTotals.map((s) => [s.state, s]));
    return {
      ...indiaGeo,
      features: indiaGeo.features.map((f) => {
        const st = f.properties?.__state || "";
        const row = totalsMap.get(st);
        const shops = row?.shops || 0;
        const hasAssignments = !!row?.hasAssignments;
        const match = !globalQueryLower || String(st).toLowerCase().includes(globalQueryLower);
        return {
          ...f,
          properties: {
            ...f.properties,
            __shops: shops,
            __hasAssignments: hasAssignments,
            __match: match,
          },
        };
      }),
    };
  }, [indiaGeo, stateTotals, globalQueryLower]);

  // ---------- LAYERS ----------
  const stateFillLayer = useMemo(
    () => ({
      id: "states-fill",
      type: "fill",
      paint: {
        "fill-color": [
          "case",
          ["==", ["get", "__match"], true],
          ["get", "__color"],
          "rgba(0,0,0,0)",
        ],
        "fill-opacity": [
          "case",
          ["==", ["get", "__match"], true],
          0.85,
          0.15,
        ],
      },
    }),
    []
  );

  const stateLineLayer = useMemo(
    () => ({
      id: "states-line",
      type: "line",
      paint: {
        "line-color": [
          "case",
          ["==", ["get", "__hasAssignments"], true],
          "#0f172a",
          "#ffffff",
        ],
        "line-width": [
          "case",
          ["==", ["get", "__hasAssignments"], true],
          1.5,
          1,
        ],
        "line-opacity": 0.9,
      },
    }),
    []
  );

  const districtFillLayer = useMemo(
    () => ({
      id: "districts-fill",
      type: "fill",
      paint: {
        "fill-color": [
          "match",
          ["get", "__status"],
          "ASSIGNED",
          ["get", "__colorAssigned"],
          "UNASSIGNED",
          "#fbbf24",
          "NO DATA",
          "#e2e8f0",
          "#e2e8f0",
        ],
        "fill-opacity": [
          "case",
          ["==", ["get", "__hit"], true],
          0.88,
          0.18,
        ],
      },
    }),
    []
  );

  const districtLineLayer = useMemo(
    () => ({
      id: "districts-line",
      type: "line",
      paint: {
        "line-color": "#ffffff",
        "line-width": 1,
        "line-opacity": 0.9,
      },
    }),
    []
  );

  // Decorate colors into geojson properties (because expressions can't call JS scale directly)
  const indiaGeoForMap = useMemo(() => {
    if (!indiaGeoDecorated) return null;
    return {
      ...indiaGeoDecorated,
      features: indiaGeoDecorated.features.map((f) => {
        const shops = Number(f.properties?.__shops || 0);
        return { ...f, properties: { ...f.properties, __color: stScale(shops) } };
      }),
    };
  }, [indiaGeoDecorated, stScale]);

  const districtGeoForMap = useMemo(() => {
    if (!districtGeoFiltered) return null;
    return {
      ...districtGeoFiltered,
      features: districtGeoFiltered.features.map((f) => {
        const shops = Number(f.properties?.__shops || 0);
        return {
          ...f,
          properties: {
            ...f.properties,
            __colorAssigned: distScale(Math.max(1, shops)),
          },
        };
      }),
    };
  }, [districtGeoFiltered, distScale]);

  // ---------- CLICK HANDLERS ----------
  const onStateClick = (e) => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const features = map.queryRenderedFeatures(e.point, { layers: ["states-fill"] });
    const f = features?.[0];
    const st = f?.properties?.__state;
    if (!st) return;
    setSelectedState(st);
    setDistrictQuery("");
    setStatusFilter("ALL");
    setShowPins(true);
  };

  const onDistrictHover = (e) => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const features = map.queryRenderedFeatures(e.point, { layers: ["districts-fill"] });
    const f = features?.[0];
    if (!f) {
      setHover(null);
      return;
    }
    const p = f.properties || {};
    const district = p.__district;
    const shops = Number(p.__shops || 0);
    const status = p.__status || "NO DATA";
    const key = `${selectedState}__${district}`;
    const empList = assignmentByDistrict.get(key) || [];
    setHover({
      type: "district",
      state: selectedState,
      district,
      shops,
      status,
      employees: empList,
    });
  };

  const onDistrictClick = (e) => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const features = map.queryRenderedFeatures(e.point, { layers: ["districts-fill"] });
    const f = features?.[0];
    if (!f) return;
    const district = f.properties?.__district;
    if (!district) return;
    setDistrictQuery(district);
    setStatusFilter("ALL");
  };

  // ---------- UI LISTS ----------
  const filteredStateList = useMemo(() => {
    if (!globalQueryLower) return stateTotals;
    return stateTotals.filter((s) => String(s.state).toLowerCase().includes(globalQueryLower));
  }, [stateTotals, globalQueryLower]);

  const districtRowsForPanel = useMemo(() => {
    if (!filteredDistrictFeatures.length) return [];
    return filteredDistrictFeatures
      .map((f) => ({
        district: f.properties?.__district,
        shops: Number(f.properties?.__shops || 0),
        status: f.properties?.__status,
      }))
      .sort((a, b) => (b.shops || 0) - (a.shops || 0));
  }, [filteredDistrictFeatures]);

  // ---------- RENDER ----------
  return (
    <div className="w-full min-h-[calc(100vh-24px)] bg-gradient-to-b from-slate-50 via-slate-50 to-white p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-[0_10px_30px_rgba(37,99,235,0.25)]" />
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
              Ops Allocation Map
            </h1>
            {/* <p className="text-sm text-slate-600">
              Search state/district (Enter). Click state → district view. Assigned/Unassigned/No-data highlighted.
            </p> */}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <input
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runGlobalSearch()}
              placeholder="Search state or district... (Enter)"
              className="w-[260px] max-w-[80vw] rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            {globalQuery ? (
              <button
                onClick={() => setGlobalQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700"
                title="Clear search"
              >
                ✕
              </button>
            ) : null}
          </div>

          <button
            onClick={() => {
              setSelectedState("All India");
              setDistrictQuery("");
              setStatusFilter("ALL");
              setShowPins(true);
              setHover(null);
              const map = mapRef.current?.getMap?.();
              if (map) map.flyTo({ center: [82.8, 22.7], zoom: 4, duration: 500 });
            }}
            className={cn(
              "px-3 py-2 text-sm rounded-2xl border transition shadow-sm",
              selectedState === "All India"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white/80 text-slate-800 border-slate-200/70 hover:bg-slate-100"
            )}
          >
            All India
          </button>

          <div className="px-3 py-2 text-sm rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 shadow-sm">
            <span className="text-slate-500">Selected: </span>
            <span className="font-medium text-slate-900">{selectedState}</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
     <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-4 md:mb-6">
      <Card label="Total Shops" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.total_shops ?? 0} hint="Onboarded (district-wise)" />
      <Card label="States" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.states ?? 0} hint="Covered states" />
      <Card label="Districts" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.districts ?? 0} hint="Covered districts" />
      <Card label="Employees" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.employees?.total ?? 0} hint="Total employees"/>
      <Card label="Managers" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.employees?.operation ?? 0} hint="Operations team"/>
      <Card label="Executives" value={dashboardLoading ? <ValueLoader /> : mapDashboard?.employees?.sales ?? 0} hint="Sales team"/>
</div>



      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-4">
        {/* MAP */}
        <div className="relative rounded-3xl border border-slate-200/70 bg-white/70 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.08)] overflow-hidden">
          <div className="p-3 md:p-4 border-b border-slate-200/60 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm font-medium text-slate-900">
              {selectedState === "All India" ? "India (States)" : `${selectedState} (Districts)`}
            </div>

            {selectedState !== "All India" ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={districtQuery}
                  onChange={(e) => setDistrictQuery(e.target.value)}
                  placeholder="Search district..."
                  className="w-full md:w-60 rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />

                <div className="flex items-center gap-1 rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur p-1">
                  {[
                    { k: "ALL", t: "All" },
                    { k: "ASSIGNED", t: "Assigned" },
                    { k: "UNASSIGNED", t: "Unassigned" },
                    { k: "NO DATA", t: "No data" },
                  ].map((x) => (
                    <button
                      key={x.k}
                      onClick={() => setStatusFilter(x.k)}
                      className={cn(
                        "px-3 py-1.5 text-xs rounded-xl transition",
                        statusFilter === x.k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                      )}
                    >
                      {x.t}
                    </button>
                  ))}
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-700 px-3 py-2 rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur shadow-sm">
                  <input
                    type="checkbox"
                    checked={showPins}
                    onChange={(e) => setShowPins(e.target.checked)}
                    className="accent-blue-600"
                  />
                  Pins
                </label>
              </div>
            ) : (
              <div className="text-xs text-slate-500">Click a state to open district view</div>
            )}
          </div>

          <div className="p-2 md:p-4">
            <div className="w-full aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden">
              <MapGL
                ref={mapRef}
                mapLib={maplibregl}
                initialViewState={{ longitude: 82.8, latitude: 22.7, zoom: 4 }}
                mapStyle={MAP_STYLE}
                attributionControl={false}
                onClick={(e) => {
                  if (selectedState === "All India") onStateClick(e);
                  else onDistrictClick(e);
                }}
                onMouseMove={(e) => {
                  setMousePos({ x: e.point.x, y: e.point.y });

                  if (selectedState !== "All India") {
                    onDistrictHover(e);
                  }
                }}
                onMouseLeave={() => setHover(null)}

                cursor={selectedState === "All India" ? "pointer" : "default"}
              >

                <NavigationControl position="top-right" showCompass={false} />

                {selectedState === "All India" ? (
                  indiaGeoForMap ? (
                    <Source
                      key="india-source"
                      id="india"
                      type="geojson"
                      data={indiaGeoForMap}
                    >
                      <Layer {...stateFillLayer} />
                      <Layer {...stateLineLayer} />
                    </Source>
                  ) : null
                ) : districtGeoForMap ? (
                  <Source
                    key={`districts-source-${selectedState}`}
                    id="districts"
                    type="geojson"
                    data={districtGeoForMap}
                  >
                    <Layer {...districtFillLayer} />
                    <Layer {...districtLineLayer} />
                  </Source>
                ) : null}

                {/* MOUSE HOVER TOOLTIP */}
                {hover && hover.type === "district" && (
                  <div
                    className="pointer-events-none absolute z-50"
                    style={{
                      left: mousePos.x + 14,
                      top: mousePos.y + 14,
                    }}
                  >
                    <div className="max-w-xs rounded-xl border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs">
                      {/* Header */}
                      <div className="font-semibold text-slate-900">
                        {hover.district}, {hover.state}
                      </div>

                      {/* Shops + Status */}
                      <div className="mt-1 flex items-center gap-2 text-slate-600">
                        <span>
                          Shops: <span className="font-semibold">{hover.shops}</span>
                        </span>
                        {hover.status && <StatusPill status={hover.status} />}
                      </div>

                      {/* Employees */}
                      <div className="mt-2">
                        {hover.employees?.length ? (
                          <>
                            <div className="text-[11px] text-slate-500 mb-1">
                              Assigned Employees
                            </div>

                            <div className="flex flex-wrap gap-1">
                              {hover.employees.slice(0, 4).map((emp, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px]"
                                >
                                  {emp.name}
                                  {emp.position && (
                                    <span className="ml-1 text-slate-500">
                                      ({emp.position})
                                    </span>
                                  )}
                                </span>
                              ))}

                              {hover.employees.length > 4 && (
                                <span className="px-2 py-1 rounded-full bg-slate-900 text-white text-[11px]">
                                  +{hover.employees.length - 4} more
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-[11px] text-slate-500">
                            No employee assigned
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}



                {/* Pins (constant size, readable at any zoom) */}
                {selectedState !== "All India" && showPins
                  ? districtPins
                      .filter((p) => (statusFilter === "ALL" ? true : p.status === statusFilter))
                      .map((p) => (
                        <Marker key={`${p.state}__${p.district}`} longitude={p.lng} latitude={p.lat} anchor="bottom">
                          <div
                            className="relative"
                            onMouseEnter={() =>
                              setHover({
                                type: "pin",
                                state: p.state,
                                district: p.district,
                                shops: p.shops,
                                status: p.status,
                                employees: p.employees,
                              })
                            }
                            onMouseLeave={() => setHover(null)}
                          >
                            {/* pin */}
                            <div className="h-8 w-8 rounded-full bg-blue-600/15 flex items-center justify-center">
                              <div className="h-4 w-4 rounded-full bg-blue-600 border-2 border-white shadow" />
                            </div>
                            {/* label */}
                            <div className="mt-1 whitespace-nowrap text-[10px] font-medium text-slate-900 bg-white/80 backdrop-blur px-2 py-1 rounded-full border border-slate-200 shadow-sm">
                              {p.district}
                            </div>
                          </div>
                        </Marker>
                      ))
                  : null}
              </MapGL>
            </div>

            {/* Loading / error */}
            <div className="mt-3 flex items-center justify-between text-xs">
              <div className="text-slate-600">
                {loading ? "Loading map data..." : err ? <span className="text-rose-600">{err}</span> : "Ready"}
              </div>
              {selectedState !== "All India" ? (
                <button
                  onClick={() => {
                    setSelectedState("All India");
                    setDistrictQuery("");
                    setStatusFilter("ALL");
                    setHover(null);
                    const map = mapRef.current?.getMap?.();
                    if (map) map.flyTo({ center: [82.8, 22.7], zoom: 4, duration: 500 });
                  }}
                  className="text-xs px-3 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
                >
                  Back to India
                </button>
              ) : null}
            </div>
          </div>

          {/* Floating Tooltip */}
          <div className="absolute left-3 bottom-3 right-3 md:left-4 md:right-4 md:bottom-4 mb-5">
            <div className="rounded-3xl border border-slate-200/70 bg-white/80 backdrop-blur-xl px-3 py-2 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
              {hover ? (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {hover.type === "pin" || hover.type === "district"
                        ? `${hover.district}, ${hover.state}`
                        : `${hover.state}`}
                    </div>
                    <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                      <span>
                        Shops: <span className="font-semibold">{hover.shops || 0}</span>
                      </span>
                      {hover.status ? <StatusPill status={hover.status} /> : null}
                    </div>
                  </div>

                  {Array.isArray(hover.employees) && hover.employees.length ? (
                    <div className="flex flex-wrap gap-2">
                      {hover.employees.slice(0, 4).map((e, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 rounded-full bg-slate-100/80 text-slate-700 border border-slate-200"
                          title={`${e.position}${e.assignType ? ` • ${e.assignType}` : ""}`}
                        >
                          {e.name}
                          {e.reportTo ? <span className="ml-2 text-[10px] text-slate-500">↳ {e.reportTo}</span> : null}
                        </span>
                      ))}
                      {hover.employees.length > 4 ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-900 text-white">
                          +{hover.employees.length - 4} more
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600">
                      {selectedState === "All India" ? "Click a state" : "No employee assigned"}
                    </div>
                  )}
                </div>
              ) : (
                // <div className="text-xs text-slate-600">
                //   {selectedState === "All India"
                //     ? "Search a state (Enter) or click a state to view districts."
                //     : "Hover districts or pins for details. Click a district to auto-filter it."}
                // </div>
                <></>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="rounded-3xl border border-slate-200/70 bg-white/70 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.08)] overflow-hidden">
          <div className="p-3 md:p-4 border-b border-slate-200/60 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {selectedState === "All India" ? "State Totals" : `${selectedState} • Districts`}
              </div>
              <div className="text-xs text-slate-500">
                {selectedState === "All India"
                  ? `Showing ${filteredStateList.length} states`
                  : `Showing ${districtRowsForPanel.length} district(s)`}
              </div>
            </div>

            {selectedState !== "All India" ? (
              <button
                onClick={() => {
                  setDistrictQuery("");
                  setStatusFilter("ALL");
                }}
                className="text-xs px-3 py-2 rounded-2xl border border-slate-200/70 bg-white/80 hover:bg-slate-50 shadow-sm"
              >
                Reset
              </button>
            ) : null}
          </div>

          <div className="p-3 md:p-4 space-y-3">
            {selectedState === "All India" ? (
              <div className="space-y-2">
                {filteredStateList.map((s) => (
                  <button
                    key={s.state}
                    onClick={() => {
                      setSelectedState(s.state);
                      setDistrictQuery("");
                      setStatusFilter("ALL");
                      setShowPins(true);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-2xl border border-slate-200/70 bg-white/70 hover:bg-white shadow-sm transition"
                  >
                    <span className="font-medium text-slate-900">{s.state}</span>
                    <span className="text-slate-600 text-sm">
                      {s.shops} shops • {s.hasAssignments ? "Assigned" : "No assignments"}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <AnimatePresence initial={false}>
                  {districtRowsForPanel.map((r) => (
                    <motion.div
                      key={`${selectedState}-${r.district}-${r.status}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="rounded-3xl border border-slate-200/70 bg-white/80 backdrop-blur overflow-hidden shadow-sm"
                    >
                      <div className="px-3 py-2 bg-slate-50/70 border-b border-slate-200/60 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{r.district}</div>
                          <div className="text-xs text-slate-500">{selectedState}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500">Shops</div>
                          <div className="text-lg font-semibold text-slate-900">{r.shops}</div>
                        </div>
                      </div>

                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-600">Status</span>
                          <StatusPill status={r.status} />
                        </div>

                        <button
                          onClick={() => setDistrictQuery(r.district)}
                          className="text-xs px-3 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
                        >
                          Focus this district
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {districtRowsForPanel.length === 0 ? (
                  <div className="text-sm text-slate-600">No districts match your filters.</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* <div className="mt-4 text-xs text-slate-500">
        ✅ Pins auto-use district centroids. For best accuracy, return <span className="font-mono">lat</span> &{" "}
        <span className="font-mono">lng</span> from API per district.
      </div> */}
    </div>
  );
}



// import React, { useEffect, useMemo, useRef, useState } from "react";
// import {
//   ComposableMap,
//   Geographies,
//   Geography,
//   ZoomableGroup,
//   Marker,
// } from "react-simple-maps";
// import { scaleQuantize } from "d3-scale";
// import { geoBounds, geoCentroid } from "d3-geo";
// import { AnimatePresence, motion } from "framer-motion";

// // -------------------- TOPOJSON URLS --------------------
// const INDIA_STATES_TOPOJSON_URL =
//   "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@8d907bc/topojson/india.json";

// function stateToDistrictTopoUrl(stateName) {
//   const slug = String(stateName || "")
//     .trim()
//     .toLowerCase()
//     .replace(/&/g, "and")
//     .replace(/\s+/g, "-")
//     .replace(/--+/g, "-");

//   return `https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@8d907bc/topojson/states/${slug}.json`;
// }

// // -------------------- MOCK DATA (REPLACE WITH YOUR API) --------------------
// // Shops onboarded district-wise (source of truth for coverage)
// const mockDistrictShops = [
//   { state: "Maharashtra", district: "Sangli", shops: 250 },
//   { state: "Maharashtra", district: "Satara", shops: 240 },
//   { state: "Maharashtra", district: "Pune", shops: 180 },
//   { state: "Maharashtra", district: "Kolhapur", shops: 90 },

//   { state: "Rajasthan", district: "Jaipur", shops: 120 },
//   { state: "Rajasthan", district: "Jodhpur", shops: 70 },
//   { state: "Rajasthan", district: "Udaipur", shops: 55 },

//   { state: "Madhya Pradesh", district: "Raipur", shops: 100 },
// ];

// // Your assignment payload (supports STATE + DISTRICT + report_to)
// const apiEmployeePayload = [
//   {
//     employee_name: "Sujit Hankare",
//     position: "Manager",
//     assign_type: "STATE",
//     assign_name: ["Maharashtra", "Rajstan"],
//     total_shops: 500,
//     areas: [
//       { state: "Maharashtra", district: "Sangli", shops: 200 },
//       { state: "Maharashtra", district: "Satara", shops: 100 },
//       { state: "Rajstan", district: "Jaipur", shops: 200 },
//     ],
//   },
//   {
//     employee_name: "Indresh Bhai",
//     position: "Manager",
//     assign_type: "STATE",
//     assign_name: ["Madhya Pradesh"],
//     total_shops: 100,
//     areas: [{ state: "Madhya Pradesh", district: "Raipur", shops: 100 }],
//   },
//   {
//     employee_name: "Tarun Bhai",
//     position: "Executive",
//     assign_type: "DISTRICT",
//     assign_name: ["Raipur"],
//     total_shops: 100,
//     report_to: "Indresh Bhai",
//     report_to_state: "Madhya Pradesh",
//     areas: [{ state: "Madhya Pradesh", district: "Raipur", shops: 100 }],
//   },
// ];

// // Optional district coords for pinpoints (lon, lat) fallback
// const AREA_COORDS = {
//   sangali: [74.5642, 16.8524],
//   sangli: [74.5642, 16.8524],
//   satara: [73.9984, 17.6805],
//   pune: [73.8567, 18.5204],
//   kolhapur: [74.2433, 16.705],
//   jaipur: [75.7873, 26.9124],
//   jodhpur: [73.0243, 26.2389],
//   udaipur: [73.7125, 24.5854],
//   raipur: [81.6296, 21.2514],
// };

// // -------------------- HELPERS --------------------
// function cn(...c) {
//   return c.filter(Boolean).join(" ");
// }

// function titleCase(str) {
//   return String(str || "")
//     .toLowerCase()
//     .split(" ")
//     .filter(Boolean)
//     .map((w) => (w[0] ? w[0].toUpperCase() + w.slice(1) : ""))
//     .join(" ");
// }

// function normalizeStateName(s) {
//   if (!s) return "";
//   const x = String(s).trim().toLowerCase();
//   const map = {
//     rajstan: "Rajasthan",
//     rajasthan: "Rajasthan",
//     maharastra: "Maharashtra",
//     maharashtra: "Maharashtra",
//     "madhya pradesh": "Madhya Pradesh",
//     mp: "Madhya Pradesh",
//   };
//   return map[x] || titleCase(String(s).trim());
// }

// function normalizeDistrictName(s) {
//   if (!s) return "";
//   const x = String(s).trim().toLowerCase();
//   const map = { sangali: "Sangli", sangli: "Sangli" };
//   return map[x] || titleCase(String(s).trim());
// }

// function normalizeAssignType(v) {
//   const x = String(v || "").trim().toUpperCase();
//   if (x === "STATE" || x === "S" || x === "ST") return "STATE";
//   if (x === "DISTRICT" || x === "D" || x === "DT") return "DISTRICT";
//   return x || "STATE";
// }

// function normalizeAreaKey(area) {
//   return String(area || "").trim().toLowerCase();
// }

// function getGeoStateName(geo) {
//   const p = geo?.properties || {};
//   return (
//     p.st_nm ||
//     p.ST_NM ||
//     p.state ||
//     p.STATE ||
//     p.name ||
//     p.NAME ||
//     p.NAME_1 ||
//     p.admin ||
//     p.ADMIN ||
//     ""
//   );
// }

// function getGeoDistrictName(geo) {
//   const p = geo?.properties || {};
//   return (
//     p.district ||
//     p.DISTRICT ||
//     p.dtname ||
//     p.DTNAME ||
//     p.district_name ||
//     p.DISTRICT_NAME ||
//     p.name ||
//     p.NAME ||
//     ""
//   );
// }

// function getDistrictPoint(districtName, lat, lng) {
//   if (
//     lat !== undefined &&
//     lng !== undefined &&
//     Number.isFinite(Number(lat)) &&
//     Number.isFinite(Number(lng))
//   ) {
//     return [Number(lng), Number(lat)];
//   }
//   const key = normalizeAreaKey(districtName);
//   return AREA_COORDS[key] || null;
// }

// function fitZoomFromBounds(bounds) {
//   if (!bounds) return 1;
//   const [[minLon], [maxLon]] = bounds;
//   const span = Math.max(0.01, Math.abs(maxLon - minLon));
//   const z = 12 / span;
//   return Math.max(1, Math.min(6, z));
// }

// function deriveStatus({ shops, hasShopRow, employeesCount }) {
//   if (employeesCount > 0) return "ASSIGNED";
//   if (hasShopRow && shops > 0) return "UNASSIGNED";
//   return "NO DATA";
// }

// // Flatten your new payload into district-level rows
// function flattenEmployeePayload(payload) {
//   const out = [];
//   for (const row of payload || []) {
//     const name = row?.employee_name || row?.name || "—";
//     const position = row?.position || "—";
//     const assignType = normalizeAssignType(row?.assign_type);
//     const reportTo = row?.report_to || null;

//     const areas = Array.isArray(row?.areas) ? row.areas : [];
//     if (areas.length) {
//       for (const a of areas) {
//         out.push({
//           name,
//           position,
//           assignType,
//           reportTo,
//           state: normalizeStateName(a?.state),
//           district: normalizeDistrictName(a?.district || a?.area || ""),
//           shops: Number(a?.shops || 0),
//           lat: a?.lat,
//           lng: a?.lng,
//         });
//       }
//       continue;
//     }

//     out.push({
//       name,
//       position,
//       assignType,
//       reportTo,
//       state: normalizeStateName(row?.state),
//       district: normalizeDistrictName(row?.district || row?.area || ""),
//       shops: Number(row?.shops || row?.total_shops || 0),
//       lat: row?.lat,
//       lng: row?.lng,
//     });
//   }
//   return out;
// }

// function Card({ label, value, hint }) {
//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 8 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.35 }}
//       className="rounded-3xl border border-slate-200/70 bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(15,23,42,0.06)] p-4"
//     >
//       <div className="text-xs font-medium text-slate-500">{label}</div>
//       <div className="text-2xl font-semibold tracking-tight text-slate-900 mt-1">
//         {value}
//       </div>
//       {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
//     </motion.div>
//   );
// }

// function StatusPill({ status }) {
//   const cls =
//     status === "ASSIGNED"
//       ? "bg-blue-50 text-blue-700 border-blue-200"
//       : status === "UNASSIGNED"
//       ? "bg-amber-50 text-amber-700 border-amber-200"
//       : "bg-slate-50 text-slate-700 border-slate-200";
//   return (
//     <span className={cn("text-[11px] px-2 py-1 rounded-full border", cls)}>
//       {status}
//     </span>
//   );
// }

// // -------------------- MAIN COMPONENT --------------------
// export default function OpsAllocationDistrictMap() {
//   const fitRef = useRef({ key: null });
//   const suppressMoveRef = useRef(false);

//   const [selectedState, setSelectedState] = useState("All India");
//   const [hover, setHover] = useState(null);
//   const [mapCenter, setMapCenter] = useState([82.8, 22.7]);
//   const [mapZoom, setMapZoom] = useState(1);

//   const [indiaTopo, setIndiaTopo] = useState(null);
//   const [districtTopo, setDistrictTopo] = useState(null);
//   const [geoLoading, setGeoLoading] = useState(false);
//   const [geoError, setGeoError] = useState(null);

//   // Controls
//   const [districtQuery, setDistrictQuery] = useState("");
//   const [statusFilter, setStatusFilter] = useState("ALL");
//   const [showPins, setShowPins] = useState(true);

//   // Global search: state or district
//   const [globalQuery, setGlobalQuery] = useState("");

//   // ✅ Pin size behavior:
//   // react-simple-maps markers scale with zoom. To keep pins readable,
//   // counter-scale with inverse zoom (screen-size stable).
//   const pinScale = useMemo(() => {
//     const base = 1.6;
//     const s = base / Math.max(1, mapZoom);
//     return Math.max(0.6, Math.min(1.6, s));
//   }, [mapZoom]);

//   useEffect(() => {
//     fitRef.current.key = null;
//   }, [selectedState]);

//   async function fetchJson(url) {
//     const res = await fetch(url, { cache: "force-cache" });
//     if (!res.ok) throw new Error(`Failed to load map data (${res.status})`);
//     return await res.json();
//   }

//   // Load India map
//   useEffect(() => {
//     let alive = true;
//     (async () => {
//       try {
//         setGeoError(null);
//         setGeoLoading(true);
//         const data = await fetchJson(INDIA_STATES_TOPOJSON_URL);
//         if (!alive) return;
//         setIndiaTopo(data);
//       } catch (e) {
//         if (!alive) return;
//         setGeoError(e?.message || "Failed to load India map");
//       } finally {
//         if (!alive) return;
//         setGeoLoading(false);
//       }
//     })();
//     return () => {
//       alive = false;
//     };
//   }, []);

//   // Load district topo when state selected
//   useEffect(() => {
//     let alive = true;
//     (async () => {
//       if (selectedState === "All India") {
//         setDistrictTopo(null);
//         return;
//       }
//       try {
//         setGeoError(null);
//         setGeoLoading(true);
//         const url = stateToDistrictTopoUrl(selectedState);
//         const data = await fetchJson(url);
//         if (!alive) return;
//         setDistrictTopo(data);
//       } catch (e) {
//         if (!alive) return;
//         setDistrictTopo(null);
//         setGeoError(
//           e?.message ||
//             `District map not available for ${selectedState}. Use local topojson in production.`
//         );
//       } finally {
//         if (!alive) return;
//         setGeoLoading(false);
//       }
//     })();
//     return () => {
//       alive = false;
//     };
//   }, [selectedState]);

//   // Normalize datasets
//   const districtShops = useMemo(() => {
//     return (mockDistrictShops || []).map((d) => ({
//       state: normalizeStateName(d.state),
//       district: normalizeDistrictName(d.district),
//       shops: Number(d.shops || 0),
//       lat: d.lat,
//       lng: d.lng,
//     }));
//   }, []);

//   const assignments = useMemo(() => flattenEmployeePayload(apiEmployeePayload), []);
//   const globalQueryLower = globalQuery.trim().toLowerCase();

//   const assignmentByDistrict = useMemo(() => {
//     const m = new Map();
//     for (const a of assignments) {
//       const key = `${a.state}__${a.district}`;
//       if (!m.has(key)) m.set(key, []);
//       m.get(key).push(a);
//     }
//     return m;
//   }, [assignments]);

//   const shopsByDistrict = useMemo(() => {
//     const m = new Map();
//     for (const d of districtShops) {
//       const key = `${d.state}__${d.district}`;
//       const prev = m.get(key) || { ...d, shops: 0 };
//       prev.shops += d.shops;
//       if (d.lat !== undefined) prev.lat = d.lat;
//       if (d.lng !== undefined) prev.lng = d.lng;
//       m.set(key, prev);
//     }
//     return m;
//   }, [districtShops]);

//   const stateTotals = useMemo(() => {
//     const m = new Map();
//     for (const d of districtShops) {
//       if (!m.has(d.state)) m.set(d.state, { state: d.state, shops: 0 });
//       m.get(d.state).shops += d.shops;
//     }
//     const assignedStates = new Set(assignments.map((a) => a.state));
//     return [...m.values()]
//       .map((x) => ({ ...x, hasAssignments: assignedStates.has(x.state) }))
//       .sort((a, b) => b.shops - a.shops);
//   }, [districtShops, assignments]);

//   // Global search resolve: state OR district
//   const resolveSearchTarget = useMemo(() => {
//     if (!globalQueryLower) return null;

//     const exactState = stateTotals.find(
//       (s) => String(s.state).toLowerCase() === globalQueryLower
//     );
//     if (exactState) return { type: "STATE", state: exactState.state };

//     const partialState = stateTotals.find((s) =>
//       String(s.state).toLowerCase().includes(globalQueryLower)
//     );
//     if (partialState) return { type: "STATE", state: partialState.state };

//     const districtFromShops = districtShops.find((d) =>
//       String(d.district).toLowerCase().includes(globalQueryLower)
//     );
//     if (districtFromShops)
//       return {
//         type: "DISTRICT",
//         state: districtFromShops.state,
//         district: districtFromShops.district,
//       };

//     const districtFromAssign = assignments.find((a) =>
//       String(a.district).toLowerCase().includes(globalQueryLower)
//     );
//     if (districtFromAssign)
//       return {
//         type: "DISTRICT",
//         state: districtFromAssign.state,
//         district: districtFromAssign.district,
//       };

//     return null;
//   }, [globalQueryLower, stateTotals, districtShops, assignments]);

//   const runGlobalSearch = () => {
//     const t = resolveSearchTarget;
//     if (!t) return;

//     if (t.type === "STATE") {
//       setSelectedState(t.state);
//       setHover(null);
//       setDistrictQuery("");
//       setStatusFilter("ALL");
//       setShowPins(true);
//       setMapCenter([82.8, 22.7]);
//       setMapZoom(1);
//       return;
//     }

//     if (t.type === "DISTRICT") {
//       setSelectedState(t.state);
//       setHover(null);
//       setDistrictQuery(t.district);
//       setStatusFilter("ALL");
//       setShowPins(true);
//       setMapCenter([82.8, 22.7]);
//       setMapZoom(1);
//     }
//   };

//   const allStatesFiltered = useMemo(() => {
//     if (!globalQueryLower) return stateTotals;
//     return stateTotals.filter((s) =>
//       String(s.state).toLowerCase().includes(globalQueryLower)
//     );
//   }, [stateTotals, globalQueryLower]);

//   const allIndia = useMemo(() => {
//     const totalShops = districtShops.reduce((s, d) => s + d.shops, 0);
//     const statesCovered = new Set(districtShops.map((d) => d.state)).size;
//     const districtsCovered = new Set(
//       districtShops.map((d) => `${d.state}__${d.district}`)
//     ).size;
//     const employees = new Set(assignments.map((a) => a.name)).size;
//     const managers = new Set(
//       assignments
//         .filter((a) => String(a.position).toLowerCase().includes("manager"))
//         .map((a) => a.name)
//     ).size;
//     const executives = new Set(
//       assignments
//         .filter((a) => String(a.position).toLowerCase().includes("executive"))
//         .map((a) => a.name)
//     ).size;

//     return { totalShops, statesCovered, districtsCovered, employees, managers, executives };
//   }, [districtShops, assignments]);

//   const maxStateShops = useMemo(() => {
//     let max = 0;
//     for (const s of stateTotals) max = Math.max(max, s.shops);
//     return max;
//   }, [stateTotals]);

//   const stateColorScale = useMemo(() => {
//     return scaleQuantize()
//       .domain([0, Math.max(1, maxStateShops)])
//       .range([
//         "#eff6ff",
//         "#dbeafe",
//         "#bfdbfe",
//         "#93c5fd",
//         "#60a5fa",
//         "#3b82f6",
//         "#2563eb",
//         "#1d4ed8",
//       ]);
//   }, [maxStateShops]);

//   const selectedStateDistrictRows = useMemo(() => {
//     if (selectedState === "All India") return [];

//     const rows = [];

//     for (const [key, d] of shopsByDistrict.entries()) {
//       if (!key.startsWith(`${selectedState}__`)) continue;
//       const empList = assignmentByDistrict.get(key) || [];
//       rows.push({
//         state: d.state,
//         district: d.district,
//         shops: d.shops,
//         employees: empList,
//         lat: d.lat,
//         lng: d.lng,
//         __hasShopRow: true,
//       });
//     }

//     for (const [key, empList] of assignmentByDistrict.entries()) {
//       if (!key.startsWith(`${selectedState}__`)) continue;
//       if (shopsByDistrict.has(key)) continue;
//       const [, district] = key.split("__");
//       rows.push({
//         state: selectedState,
//         district,
//         shops: 0,
//         employees: empList,
//         __hasShopRow: false,
//       });
//     }

//     return rows
//       .map((r) => {
//         const status = deriveStatus({
//           shops: r.shops || 0,
//           hasShopRow: !!r.__hasShopRow,
//           employeesCount: r.employees?.length || 0,
//         });
//         return { ...r, status };
//       })
//       .sort((a, b) => (b.shops || 0) - (a.shops || 0));
//   }, [selectedState, shopsByDistrict, assignmentByDistrict]);

//   const maxDistrictShops = useMemo(() => {
//     let max = 0;
//     for (const r of selectedStateDistrictRows) max = Math.max(max, r.shops || 0);
//     return max;
//   }, [selectedStateDistrictRows]);

//   const districtBlueScale = useMemo(() => {
//     return scaleQuantize()
//       .domain([0, Math.max(1, maxDistrictShops)])
//       .range([
//         "#eff6ff",
//         "#dbeafe",
//         "#bfdbfe",
//         "#93c5fd",
//         "#60a5fa",
//         "#3b82f6",
//         "#2563eb",
//         "#1d4ed8",
//       ]);
//   }, [maxDistrictShops]);

//   const filteredDistrictRows = useMemo(() => {
//     if (selectedState === "All India") return [];
//     const q = districtQuery.trim().toLowerCase();

//     return selectedStateDistrictRows.filter((r) => {
//       const okQuery = !q || String(r.district || "").toLowerCase().includes(q);
//       const okStatus =
//         statusFilter === "ALL" ||
//         (statusFilter === "NO DATA" && r.status === "NO DATA") ||
//         r.status === statusFilter;
//       return okQuery && okStatus;
//     });
//   }, [selectedState, selectedStateDistrictRows, districtQuery, statusFilter]);

//   const districtPins = useMemo(() => {
//     if (selectedState === "All India") return [];
//     return filteredDistrictRows
//       .map((r) => {
//         const p = getDistrictPoint(r.district, r.lat, r.lng);
//         if (p) return { ...r, coordinates: p };
//         return null;
//       })
//       .filter(Boolean);
//   }, [selectedState, filteredDistrictRows]);

//   const statusCounts = useMemo(() => {
//     if (selectedState === "All India") return { assigned: 0, unassigned: 0, nodata: 0 };
//     let assigned = 0;
//     let unassigned = 0;
//     let nodata = 0;
//     for (const r of selectedStateDistrictRows) {
//       if (r.status === "ASSIGNED") assigned += 1;
//       else if (r.status === "UNASSIGNED") unassigned += 1;
//       else nodata += 1;
//     }
//     return { assigned, unassigned, nodata };
//   }, [selectedState, selectedStateDistrictRows]);

//   const canRenderMap = !!indiaTopo && !geoError;

//   return (
//     <div className="w-full min-h-[calc(100vh-24px)] bg-gradient-to-b from-slate-50 via-slate-50 to-white p-4 md:p-6">
//       {/* Header */}
//       <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
//         <div className="flex items-start gap-3">
//           <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-[0_10px_30px_rgba(37,99,235,0.25)]" />
//           <div>
//             <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
//               Ops Allocation Map
//             </h1>
//             <p className="text-sm text-slate-600">
//               Search state or district (Enter). Click a state for district view.
//             </p>
//           </div>
//         </div>

//         <div className="flex items-center gap-2 flex-wrap">
//           {/* Global search */}
//           <div className="relative">
//             <input
//               value={globalQuery}
//               onChange={(e) => setGlobalQuery(e.target.value)}
//               onKeyDown={(e) => e.key === "Enter" && runGlobalSearch()}
//               placeholder="Search state or district... (Enter)"
//               className="w-[260px] max-w-[80vw] rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
//             />
//             {globalQuery ? (
//               <button
//                 onClick={() => setGlobalQuery("")}
//                 className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700"
//                 title="Clear search"
//               >
//                 ✕
//               </button>
//             ) : null}
//           </div>

//           <button
//             onClick={() => {
//               setSelectedState("All India");
//               setHover(null);
//               setDistrictQuery("");
//               setStatusFilter("ALL");
//               setShowPins(true);
//               setMapCenter([82.8, 22.7]);
//               setMapZoom(1);
//             }}
//             className={cn(
//               "px-3 py-2 text-sm rounded-2xl border transition shadow-sm",
//               selectedState === "All India"
//                 ? "bg-slate-900 text-white border-slate-900"
//                 : "bg-white/80 text-slate-800 border-slate-200/70 hover:bg-slate-100"
//             )}
//           >
//             All India
//           </button>

//           <div className="px-3 py-2 text-sm rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 shadow-sm">
//             <span className="text-slate-500">Selected: </span>
//             <span className="font-medium text-slate-900">{selectedState}</span>
//           </div>
//         </div>
//       </div>

//       {/* KPIs */}
//       <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-4 md:mb-6">
//         <Card label="Total Shops" value={allIndia.totalShops} hint="Onboarded (district-wise)" />
//         <Card label="States" value={allIndia.statesCovered} hint="With shop data" />
//         <Card label="Districts" value={allIndia.districtsCovered} hint="With shop data" />
//         <Card label="Employees" value={allIndia.employees} hint="With ≥ 1 assignment" />
//         <Card label="Managers" value={allIndia.managers} hint="By position" />
//         <Card label="Executives" value={allIndia.executives} hint="By position" />
//       </div>

//       {/* Map load error */}
//       {!canRenderMap ? (
//         <div className="rounded-3xl border border-slate-200/70 bg-white/70 p-4">
//           <div className="text-sm font-semibold text-slate-900">
//             {geoLoading ? "Loading map..." : "Map not available"}
//           </div>
//           <div className="text-xs text-slate-600 mt-1">
//             {geoLoading ? "Fetching topojson..." : geoError || "Unknown error"}
//           </div>
//         </div>
//       ) : null}

//       <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-4">
//         {/* MAP */}
//         <div className="relative rounded-3xl border border-slate-200/70 bg-white/70 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.08)] overflow-hidden">
//           <div className="p-3 md:p-4 border-b border-slate-200/60 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
//             <div className="text-sm font-medium text-slate-900">
//               {selectedState === "All India" ? "India (States)" : `${selectedState} (Districts)`}
//             </div>

//             {selectedState !== "All India" ? (
//               <div className="flex items-center gap-2 flex-wrap">
//                 <input
//                   value={districtQuery}
//                   onChange={(e) => setDistrictQuery(e.target.value)}
//                   placeholder="Search district..."
//                   className="w-full md:w-64 rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
//                 />

//                 <div className="flex items-center gap-1 rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur p-1">
//                   {[
//                     { k: "ALL", t: "All" },
//                     { k: "ASSIGNED", t: "Assigned" },
//                     { k: "UNASSIGNED", t: "Unassigned" },
//                     { k: "NO DATA", t: "No data" },
//                   ].map((x) => (
//                     <button
//                       key={x.k}
//                       onClick={() => setStatusFilter(x.k)}
//                       className={cn(
//                         "px-3 py-1.5 text-xs rounded-xl transition",
//                         statusFilter === x.k
//                           ? "bg-slate-900 text-white"
//                           : "text-slate-700 hover:bg-slate-100"
//                       )}
//                     >
//                       {x.t}
//                     </button>
//                   ))}
//                 </div>

//                 <label className="flex items-center gap-2 text-xs text-slate-700 px-3 py-2 rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur shadow-sm">
//                   <input
//                     type="checkbox"
//                     checked={showPins}
//                     onChange={(e) => setShowPins(e.target.checked)}
//                     className="accent-blue-600"
//                   />
//                   Pins
//                 </label>
//               </div>
//             ) : (
//               <div className="text-xs text-slate-500">
//                 Tip: search "Maharashtra" or "Sangli" and press Enter
//               </div>
//             )}
//           </div>

//           <div className="p-2 md:p-4">
//             <div className="w-full aspect-[4/3] md:aspect-[16/10]">
//               <ComposableMap
//                 projection="geoMercator"
//                 projectionConfig={{ center: [82.8, 22.7], scale: 900 }}
//                 style={{ width: "100%", height: "100%" }}
//               >
//                 <ZoomableGroup
//                   minZoom={1}
//                   maxZoom={6}
//                   center={mapCenter}
//                   zoom={mapZoom}
//                   onMoveEnd={(pos) => {
//                     if (suppressMoveRef.current) {
//                       suppressMoveRef.current = false;
//                       return;
//                     }
//                     if (pos?.coordinates && typeof pos.zoom === "number") {
//                       setMapCenter(pos.coordinates);
//                       setMapZoom(pos.zoom);
//                     }
//                   }}
//                 >
//                   {selectedState === "All India" ? (
//                     <Geographies geography={indiaTopo}>
//                       {({ geographies }) =>
//                         geographies.map((geo) => {
//                           const st = normalizeStateName(getGeoStateName(geo));
//                           const row = stateTotals.find((x) => x.state === st);
//                           const shops = row?.shops || 0;
//                           const hasAssignments = !!row?.hasAssignments;

//                           const match =
//                             !globalQueryLower || String(st).toLowerCase().includes(globalQueryLower);

//                           return (
//                             <Geography
//                               key={geo.rsmKey}
//                               geography={geo}
//                               onMouseEnter={() =>
//                                 setHover({ type: "state", state: st, shops, hasAssignments })
//                               }
//                               onMouseLeave={() => setHover(null)}
//                               onClick={() => {
//                                 if (!st) return;
//                                 setSelectedState(st);
//                                 setHover(null);
//                                 setDistrictQuery("");
//                                 setStatusFilter("ALL");
//                                 setShowPins(true);
//                                 setMapCenter([82.8, 22.7]);
//                                 setMapZoom(1);
//                               }}
//                               style={{
//                                 default: {
//                                   fill: stateColorScale(shops),
//                                   opacity: match ? 1 : 0.12,
//                                   stroke: hasAssignments ? "#0f172a" : "#ffffff",
//                                   strokeWidth: hasAssignments ? 1.2 : 0.8,
//                                   outline: "none",
//                                   cursor: "pointer",
//                                 },
//                                 hover: {
//                                   fill: "#111827",
//                                   opacity: 1,
//                                   stroke: "#ffffff",
//                                   strokeWidth: 0.9,
//                                   outline: "none",
//                                   cursor: "pointer",
//                                 },
//                                 pressed: {
//                                   fill: "#0b1220",
//                                   opacity: 1,
//                                   stroke: "#ffffff",
//                                   strokeWidth: 0.9,
//                                   outline: "none",
//                                 },
//                               }}
//                             />
//                           );
//                         })
//                       }
//                     </Geographies>
//                   ) : (
//                     <Geographies geography={districtTopo || indiaTopo}>
//                       {({ geographies }) => {
//                         if (geographies && geographies.length) {
//                           const fitKey = `${selectedState}__${geographies.length}`;
//                           if (fitRef.current.key !== fitKey) {
//                             fitRef.current.key = fitKey;

//                             const b = geoBounds({ type: "FeatureCollection", features: geographies });
//                             const c = geoCentroid({
//                               type: "FeatureCollection",
//                               features: geographies,
//                             });
//                             const z = Math.min(6, fitZoomFromBounds(b) * 0.85);

//                             suppressMoveRef.current = true;
//                             setMapCenter([Number(c[0].toFixed(4)), Number(c[1].toFixed(4))]);
//                             setMapZoom(Number(z.toFixed(2)));
//                           }
//                         }

//                         return (
//                           <>
//                             {geographies.map((geo) => {
//                               const dName = normalizeDistrictName(getGeoDistrictName(geo));
//                               const key = `${selectedState}__${dName}`;
//                               const shopRow = shopsByDistrict.get(key);
//                               const shops = shopRow?.shops ?? 0;
//                               const empList = assignmentByDistrict.get(key) || [];
//                               const status = deriveStatus({
//                                 shops,
//                                 hasShopRow: !!shopRow,
//                                 employeesCount: empList.length,
//                               });

//                               const fill =
//                                 status === "ASSIGNED"
//                                   ? districtBlueScale(Math.max(1, shops))
//                                   : status === "UNASSIGNED"
//                                   ? "#fbbf24"
//                                   : "#e2e8f0";

//                               const faded = statusFilter !== "ALL" && status !== statusFilter;

//                               return (
//                                 <Geography
//                                   key={geo.rsmKey}
//                                   geography={geo}
//                                   onMouseEnter={() =>
//                                     setHover({
//                                       type: "district",
//                                       state: selectedState,
//                                       district: dName,
//                                       shops,
//                                       status,
//                                       employees: empList,
//                                     })
//                                   }
//                                   onMouseLeave={() => setHover(null)}
//                                   style={{
//                                     default: {
//                                       fill,
//                                       opacity: faded ? 0.25 : 1,
//                                       stroke: "#ffffff",
//                                       strokeWidth: 0.7,
//                                       outline: "none",
//                                     },
//                                     hover: {
//                                       fill: "#0f172a",
//                                       opacity: 1,
//                                       stroke: "#ffffff",
//                                       strokeWidth: 0.8,
//                                       outline: "none",
//                                     },
//                                     pressed: {
//                                       fill: "#0b1220",
//                                       opacity: 1,
//                                       stroke: "#ffffff",
//                                       strokeWidth: 0.8,
//                                       outline: "none",
//                                     },
//                                   }}
//                                 />
//                               );
//                             })}

//                             {/* PINS */}
//                             {showPins ? (
//                               <>
//                                 {districtPins.map((p) => (
//                                   <Marker
//                                     key={`${p.state}__${p.district}`}
//                                     coordinates={p.coordinates}
//                                     onMouseEnter={() =>
//                                       setHover({
//                                         type: "pin",
//                                         state: p.state,
//                                         district: p.district,
//                                         shops: p.shops,
//                                         employees: p.employees,
//                                         status: p.status,
//                                       })
//                                     }
//                                     onMouseLeave={() => setHover(null)}
//                                   >
//                                     {/* Flag/Pin icon (screen-size stable via inverse zoom) */}
//                                     <g transform={`scale(${pinScale}) translate(-9,-28)`}>
//                                       <path
//                                         d="M9 0C4.6 0 1 3.6 1 8c0 5.4 6.2 13.6 7.1 14.7a1 1 0 0 0 1.8 0C10.8 21.6 17 13.4 17 8c0-4.4-3.6-8-8-8z"
//                                         fill="#2563eb"
//                                       />
//                                       <path
//                                         d="M8.5 6.2v8.8"
//                                         stroke="#ffffff"
//                                         strokeWidth="1.6"
//                                         strokeLinecap="round"
//                                       />
//                                       <path
//                                         d="M8.7 6.4c2.3 0 3.5.9 5 1.3v4c-1.5-.4-2.7-1.3-5-1.3"
//                                         fill="#ffffff"
//                                         opacity="0.95"
//                                       />
//                                     </g>
//                                   </Marker>
//                                 ))}
//                               </>
//                             ) : null}
//                           </>
//                         );
//                       }}
//                     </Geographies>
//                   )}
//                 </ZoomableGroup>
//               </ComposableMap>
//             </div>
//           </div>

//           {/* Floating Tooltip */}
//           <div className="absolute left-3 bottom-3 right-3 md:left-4 md:right-4 md:bottom-4">
//             <div className="rounded-3xl border border-slate-200/70 bg-white/80 backdrop-blur-xl px-3 py-2 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
//               {hover ? (
//                 <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
//                   <div>
//                     <div className="text-sm font-semibold text-slate-900">
//                       {hover.type === "state"
//                         ? `${hover.state}`
//                         : `${hover.district || "District"}, ${hover.state || ""}`}
//                     </div>
//                     <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
//                       <span>
//                         Shops: <span className="font-semibold">{hover.shops || 0}</span>
//                       </span>
//                       {hover.type !== "state" ? <StatusPill status={hover.status} /> : null}
//                       {hover.type === "state" ? (
//                         <span className="font-semibold">
//                           {hover.hasAssignments ? "Assignments present" : "No assignments"}
//                         </span>
//                       ) : null}
//                     </div>
//                   </div>

//                   {Array.isArray(hover.employees) && hover.employees.length ? (
//                     <div className="flex flex-wrap gap-2">
//                       {hover.employees.slice(0, 4).map((e, idx) => (
//                         <span
//                           key={idx}
//                           className="text-xs px-2 py-1 rounded-full bg-slate-100/80 text-slate-700 border border-slate-200"
//                           title={`${e.position}${e.assignType ? ` • ${e.assignType}` : ""}`}
//                         >
//                           {e.name}
//                           {e.reportTo ? (
//                             <span className="ml-2 text-[10px] text-slate-500">↳ {e.reportTo}</span>
//                           ) : null}
//                         </span>
//                       ))}
//                       {hover.employees.length > 4 ? (
//                         <span className="text-xs px-2 py-1 rounded-full bg-slate-900 text-white">
//                           +{hover.employees.length - 4} more
//                         </span>
//                       ) : null}
//                     </div>
//                   ) : (
//                     <div className="text-xs text-slate-600">
//                       {selectedState === "All India" ? "Click a state to view districts" : "No employee assigned"}
//                     </div>
//                   )}
//                 </div>
//               ) : (
//                 <div className="flex items-center justify-between gap-2">
//                   <div className="text-xs text-slate-600">
//                     {selectedState === "All India"
//                       ? "Search state/district and press Enter."
//                       : "Hover districts for details. Use filters for Assigned/Unassigned/No data."}
//                   </div>
//                   {selectedState !== "All India" ? (
//                     <button
//                       onClick={() => {
//                         setSelectedState("All India");
//                         setHover(null);
//                         setDistrictQuery("");
//                         setStatusFilter("ALL");
//                         setShowPins(true);
//                         setMapCenter([82.8, 22.7]);
//                         setMapZoom(1);
//                       }}
//                       className="text-xs px-3 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
//                     >
//                       Back to India
//                     </button>
//                   ) : null}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* RIGHT PANEL */}
//         <div className="rounded-3xl border border-slate-200/70 bg-white/70 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.08)] overflow-hidden">
//           <div className="p-3 md:p-4 border-b border-slate-200/60 flex items-center justify-between">
//             <div>
//               <div className="text-sm font-semibold text-slate-900">
//                 {selectedState === "All India"
//                   ? "State Totals"
//                   : `${selectedState} • District-wise Shops & Assignments`}
//               </div>
//               <div className="text-xs text-slate-500">
//                 {selectedState === "All India"
//                   ? `Showing ${allStatesFiltered.length} states`
//                   : `Showing ${filteredDistrictRows.length} district(s)`}
//               </div>
//             </div>

//             {selectedState !== "All India" ? (
//               <button
//                 onClick={() => {
//                   setSelectedState("All India");
//                   setHover(null);
//                   setDistrictQuery("");
//                   setStatusFilter("ALL");
//                   setShowPins(true);
//                   setMapCenter([82.8, 22.7]);
//                   setMapZoom(1);
//                 }}
//                 className="text-xs px-3 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
//               >
//                 Clear
//               </button>
//             ) : null}
//           </div>

//           <div className="p-3 md:p-4 space-y-3">
//             {selectedState === "All India" ? (
//               <div className="space-y-2">
//                 {allStatesFiltered.map((s) => (
//                   <button
//                     key={s.state}
//                     onClick={() => {
//                       setSelectedState(s.state);
//                       setHover(null);
//                       setDistrictQuery("");
//                       setStatusFilter("ALL");
//                       setShowPins(true);
//                       setMapCenter([82.8, 22.7]);
//                       setMapZoom(1);
//                     }}
//                     className="w-full flex items-center justify-between px-3 py-2 rounded-2xl border border-slate-200/70 bg-white/70 hover:bg-white shadow-sm transition"
//                   >
//                     <span className="font-medium text-slate-900">{s.state}</span>
//                     <span className="text-slate-600 text-sm">
//                       {s.shops} shops • {s.hasAssignments ? "Assigned" : "No assignments"}
//                     </span>
//                   </button>
//                 ))}

//                 {geoLoading ? <div className="text-xs text-slate-500">Loading map data...</div> : null}
//                 {geoError ? <div className="text-xs text-rose-600">{geoError}</div> : null}
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 <div className="grid grid-cols-3 gap-2">
//                   <div className="rounded-3xl border border-slate-200/70 bg-white/70 p-3">
//                     <div className="text-xs text-slate-600">Assigned</div>
//                     <div className="text-xl font-semibold text-slate-900">{statusCounts.assigned}</div>
//                   </div>
//                   <div className="rounded-3xl border border-slate-200/70 bg-white/70 p-3">
//                     <div className="text-xs text-slate-600">Unassigned</div>
//                     <div className="text-xl font-semibold text-slate-900">{statusCounts.unassigned}</div>
//                   </div>
//                   <div className="rounded-3xl border border-slate-200/70 bg-white/70 p-3">
//                     <div className="text-xs text-slate-600">No data</div>
//                     <div className="text-xl font-semibold text-slate-900">{statusCounts.nodata}</div>
//                   </div>
//                 </div>

//                 <AnimatePresence initial={false}>
//                   {filteredDistrictRows.map((r, idx) => (
//                     <motion.div
//                       key={`${r.state}-${r.district}-${idx}-${r.status}`}
//                       initial={{ opacity: 0, y: 10 }}
//                       animate={{ opacity: 1, y: 0 }}
//                       exit={{ opacity: 0, y: 10 }}
//                       transition={{ duration: 0.2 }}
//                       className="rounded-3xl border border-slate-200/70 bg-white/80 overflow-hidden shadow-sm"
//                     >
//                       <div className="px-3 py-2 bg-slate-50/70 border-b border-slate-200/60 flex items-center justify-between">
//                         <div>
//                           <div className="text-sm font-semibold text-slate-900">
//                             {r.district || "Unknown"}
//                           </div>
//                           <div className="text-xs text-slate-500">{r.state}</div>
//                         </div>
//                         <div className="text-right">
//                           <div className="text-xs text-slate-500">Shops</div>
//                           <div className="text-lg font-semibold text-slate-900">{r.shops || 0}</div>
//                         </div>
//                       </div>

//                       <div className="p-3">
//                         <div className="flex items-center justify-between mb-2">
//                           <span className="text-xs text-slate-600">Status</span>
//                           <StatusPill status={r.status} />
//                         </div>

//                         {r.employees?.length ? (
//                           <div className="overflow-auto">
//                             <table className="w-full text-sm">
//                               <thead>
//                                 <tr className="text-xs text-slate-500">
//                                   <th className="text-left font-medium pb-2">Employee</th>
//                                   <th className="text-left font-medium pb-2">Position</th>
//                                   <th className="text-right font-medium pb-2">District Shops</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {r.employees.map((e, ridx) => (
//                                   <tr key={ridx} className="border-t border-slate-100">
//                                     <td className="py-2 text-slate-900 font-medium">{e.name}</td>
//                                     <td className="py-2 text-slate-600">
//                                       {e.position}
//                                       {e.assignType ? (
//                                         <span className="block text-[11px] text-slate-400">
//                                           Assign: {e.assignType}
//                                         </span>
//                                       ) : null}
//                                       {e.reportTo ? (
//                                         <span className="block text-[11px] text-slate-400">
//                                           Reports to: {e.reportTo}
//                                         </span>
//                                       ) : null}
//                                     </td>
//                                     <td className="py-2 text-right font-semibold text-slate-900">
//                                       {r.shops || 0}
//                                     </td>
//                                   </tr>
//                                 ))}
//                               </tbody>
//                             </table>
//                           </div>
//                         ) : (
//                           <div className="text-sm text-slate-600">
//                             {r.status === "UNASSIGNED"
//                               ? "Shops exist but no employee assigned."
//                               : "No shops data found for this district."}
//                           </div>
//                         )}
//                       </div>
//                     </motion.div>
//                   ))}
//                 </AnimatePresence>

//                 {filteredDistrictRows.length === 0 ? (
//                   <div className="text-sm text-slate-600">No districts match your filters.</div>
//                 ) : null}
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       <div className="mt-4 text-xs text-slate-500">
//         Tip: For accurate pins, send <span className="font-mono">lat</span> &amp;{" "}
//         <span className="font-mono">lng</span> per district in API.
//       </div>
//     </div>
//   );
// }